import { useState, useRef, useCallback, useEffect } from 'react';

interface Position {
  x: number;
  y: number;
}

interface DragMetadata {
  startX: number;
  startY: number;
  startLeft: number;
  startTop: number;
}

const useDraggable = () => {
  const [position, setPosition] = useState<Position>({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const dragRef = useRef<DragMetadata | null>(null);

  const startDrag = (e: React.MouseEvent) => {
    setDragging(true);
    const startX = e.clientX;
    const startY = e.clientY;
    const startLeft = position.x;
    const startTop = position.y;
    dragRef.current = { startX, startY, startLeft, startTop };
    e.preventDefault();
  };

  const onDrag = useCallback(
    (e: MouseEvent) => {
      if (!dragging || !dragRef.current) return; // Only allow dragging in floating mode
      const { startX, startY, startLeft, startTop } = dragRef.current;
      const dx = e.clientX - startX;
      const dy = e.clientY - startY;
      setPosition({ x: startLeft + dx, y: startTop + dy });
    },
    [dragging],
  );

  const stopDrag = useCallback(() => {
    if (!dragging) return;
    setDragging(false);
    dragRef.current = null;
  }, [dragging]);

  useEffect(() => {
    if (dragging) {
      window.addEventListener('mousemove', onDrag);
      window.addEventListener('mouseup', stopDrag);
    }
    return () => {
      window.removeEventListener('mousemove', onDrag);
      window.removeEventListener('mouseup', stopDrag);
    };
  }, [dragging, onDrag, stopDrag]);

  return {
    position,
    setPosition,
    dragging,
    startDrag,
  };
};

export default useDraggable;
